/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	Router,
	UrlTree,
} from '@angular/router';
import { ApplicationService } from '@app/store/application/application.service';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { RETRIEVE_TYPES } from '@constants/api-endpoints';
import { DATADOG_EVENTS, DATADOG_PAGES } from '@constants/datadog';
import { ROUTES } from '@constants/routes';
import { datadogRum } from '@datadog/browser-rum';
import { QuoteSession } from '@models/QuoteSession';
import { UtmParameters } from '@models/UtmParameters';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { RetrieveCookieService } from '@services/retrieve-cookie.service';
import { UtmService } from '@services/utm.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class QuoteRetrievalGuard implements CanActivate {
	constructor(
		private readonly router: Router,
		private readonly sessionState: QuoteSessionQuery,
		private readonly sessionActions: QuoteSessionService,
		private readonly utmService: UtmService,
		private readonly retrieveCookieService: RetrieveCookieService,
		private readonly orchestratorService: OrchestratorService,
		private readonly appActions: ApplicationService,
	) {}

	async canActivate(
		route: ActivatedRouteSnapshot,
	): Promise<boolean | UrlTree> {
		const hasActiveSession = await firstValueFrom(
			this.sessionState.sessionId$()
		);

		// if the user still has an active session, use that and load page 2
		if (hasActiveSession) {
			return this.router.parseUrl(ROUTES.SELECT_A_POLICY);
		}

		this.appActions.setLoading(true, undefined, undefined, 'retrieve');

		const retrievalCookie = this.retrieveCookieService.getRetrieveCookie();
		let retrievedByCookie = false;

		// get the embrace_retriever cookie, and retrieve quote using that contact id
		if (retrievalCookie) {
 			await this.retrieveQuoteByContactId(retrievalCookie, route.params.retrieveType);

			this.appActions.setKameleoonEnabled(false);
			// set retrievedByCookie flag to true to skip to page 2
			// after setting UTMs and brand
			retrievedByCookie = true;
		}

		const { queryParams } = route;

		/** Handle UTM params */
		const utmValues: UtmParameters & { gclid: string } = {
			campaign: queryParams.utm_campaign ?? ``,
			channel: queryParams.utm_channel ?? ``,
			content: queryParams.utm_content ?? ``,
			medium: queryParams.utm_medium ?? ``,
			source: queryParams.utm_source ?? ``,
			term: queryParams.utm_term ?? ``,
			gclid: queryParams.gclid ?? ``,
		};

		this.utmService.setUtmValues(utmValues);
		this.sessionActions.updateUTMParams(this.utmService.getUtmCookie());

		// if we got the retrieve quote via the embrace_retriever cookie,
		// go directly to page 2
		if (retrievedByCookie) {
			this.appActions.setLoading(false, undefined, undefined, 'retrieve');

			datadogRum.addAction(DATADOG_EVENTS.LANDING_PAGE, {
				page: DATADOG_PAGES.SELECT_POLICY,
				isCookieRetrieve: true
			});

			return this.router.navigate([ROUTES.SELECT_A_POLICY], {
				state: {
					showQuoteRetrievedMessage: true
				}
			});
		}

		// try to get the brand from session, and set it on the contact
		// back up to the Embrace brand so the phone number is displayed on the page
		const sessionBrand = await firstValueFrom(this.sessionState.brand$()) || 'Embrace';

		this.sessionActions.setBrandOnContact(sessionBrand);
		this.appActions.setLoading(false, undefined, undefined, 'retrieve');

		datadogRum.addAction(DATADOG_EVENTS.LANDING_PAGE, {
			page: DATADOG_PAGES.RETRIEVE,
			isCookieRetrieve: false
		});

		// otherwise, just load the /retrieve page
		return true;
	}

	private async retrieveQuoteByContactId(contactId: string, type: string = RETRIEVE_TYPES.WEB): Promise<void> {
		try {
			const res: QuoteSession = await firstValueFrom(
				this.orchestratorService
					.retrieveQuote({
						contactId,
						retrieveType: type
					})
			);
			this.sessionActions.update(res);

			// if we get the quote here, set the brand to what is on the quote
			// and backup to Embrace if not available.
			this.sessionActions.setBrandOnContact(res?.contact?.brand ?? 'Embrace');
			this.appActions.setLastPetActive();
		} catch (error) {
			this.appActions.setLoading(false);

			if (error) {
				this.router.navigate([ROUTES.ERROR], {
					state: {
						// eslint-disable-next-line max-len
						detail: `There was an error retrieving your quote. Please try again shortly. If you continue to have issues please contact us.`,
					},
				});
			}
		}
	}
}
